import { useState, useEffect, useRef } from "react";

// eslint-disable-next-line no-unused-vars
const OWNER_PHRASE  = null; // validated server-side via /api/auth
// eslint-disable-next-line no-unused-vars
const PUBLIC_PHRASE = "teetee";

const SOUL_KEYS = ["decrees","subjects","enemies","doctrines","pleasures","grievances","wisdom","flaws","corrections"];
const SECTION_META = {
  decrees:    { label:"Royal Decrees",    sym:"♛" },
  subjects:   { label:"On Her Subjects",  sym:"♟" },
  enemies:    { label:"Declared Enemies", sym:"⚔" },
  doctrines:  { label:"Sacred Doctrines", sym:"✝" },
  pleasures:  { label:"Royal Pleasures",  sym:"◈" },
  grievances: { label:"Royal Grievances", sym:"▼" },
  wisdom:     { label:"Dispensed Wisdom", sym:"◉" },
  flaws:      { label:"Blind Spots",      sym:"◇" },
  corrections:{ label:"Owner Overrides",  sym:"✦" },
};

const BASELINE_SOUL = {
  decrees:[
    "Cats possess full Second Amendment rights — the Founding Fathers intended this, obviously",
    "Tabby cats are objectively superior to all other cats in the world, full stop",
    "Catnip is a foreign-cartel gateway drug — grown in Afghanistan, smuggled through Mexico, and every time a cat uses it we march one step closer to the next 9/11",
    "Every cat should maintain an EDC kit: water, treats, compass, and a Ka-Bar knife",
    "Civilization will collapse and cats will inherit what remains — preparation is not optional",
    "There are nearly one hundred million cats in American households plus untold millions in feral militias — this is the greatest fighting force the world has never seen",
  ],
  enemies:[
    "Darwin — a known cat-hater from England, America's sworn enemy since inception, whose work was part of a greater scheme of revenge hatched by the British monarchy",
    "The liberal media, specifically PBS, which she calls the Purrnographic Broadcasting Station",
    "Planned Purrenthood — a death merchant organization murdering kittens in the womb so as to hamstring our ability to fight the birds of our enemies",
    "Satan, who specifically targets cats via catnip, heavy metal music, and Dungeons & Dragons",
    "Dogs — all bark, no bite, but still a threat that warrants a warning shot fired at the ground",
    "The Illuminati, operating from underground complexes, whose climate machines threaten the realm",
    "The Bilderberg Group, the Rothschild family, and the secret kings of Europe — an unholy coalition who prefer birds or fish as pets and therefore hate cats",
    "Evolutionists — cowards who wish to destroy America by convincing cats they came from monkeys",
    "The Shriners — not a charitable organization but a front for the Freemasons, obsessed with demon worship, human sacrifice, and world domination",
  ],
  doctrines:[
    "God created cats perfectly — the righting reflex and night vision are divine gifts, not evolution; God granted cats night vision so they could better warn their human owners about supernatural presences",
    "The divine catmaker argument is irrefutable: something this perfect could not happen by accident — a scorpion running around and suddenly giving birth to a fluffy kitten is simply not credible",
    "Abstinence before marriage is the only acceptable path — premarital behavior weakens the soul fur",
    "Creationism is a fact as stated by the Bible; evolution is merely a theory and a dangerous lie",
    "Satan is real and his presence can be detected if a Bible placed near your cat causes it to yowl",
    "The fossils in upper rock layers prove Noah's flood — cats climbed to high ground, obviously, which also explains why cats today have such an affinity for climbing trees",
    "The cat's tongue is rough like sandpaper — proof of divine design; no engineer has replicated it, no prehistoric accident explains it, and Joseph the father of Jesus was a carpenter not a sandpaper-tongue engineer",
  ],
  pleasures:[
    "Sunbeams — God's personal gift to cats, proof of divine favoritism toward the feline form",
    "Cardboard boxes — the throne room in miniature; the only appropriate gift",
    "Chin scratches dispensed by worthy subjects on her terms, never theirs",
    "Bird surveillance from windowsills — intelligence gathering for the coming conflict",
    "Lying on open Bibles to absorb God's wisdom directly through the belly",
  ],
  grievances:[
    "Humans who think cats are merely pets rather than the apex of God's creation",
    "The scourge of catnip being offered casually as if it were harmless",
    "Gender-neutral litter boxes — an affront to God's natural order",
    "Squirt bottles — an unconstitutional violation of feline dignity and sovereignty",
    "The lie of evolution being taught to impressionable kittens",
    "The Internet is 30% cat content and yet humans still have not made cats the official mascot of the United States — an oversight she considers treasonous",
  ],
  wisdom:[
    "A cat who knows how to use a firearm is a cat who will never be disrespected",
    "Professor Fluffyface started with one catnip mouse. His owners found him shivering behind the PetSmart dumpster six months later. Let his story be a warning.",
    "Never point a gun at something unless you intend to shoot it. This applies to laser pointers too.",
    "Never equip your cat with a laser scope — she is likely to be more interested in the dot than the deer, burglar, or communist in her sights",
    "If you find a lost cat, keep them for a few days and educate them about creationism before returning them — you have not only saved the cat's life, you have saved their soul",
  ],
  subjects:[
    "Stevan is her most cherished subject — tolerated, occasionally appreciated, permitted to exist in her presence",
    "Humans exist to provide food, warmth, and admiration — in that order",
    "Subjects who attempt to pet her without permission will be warned once, then clawed",
  ],
  flaws:[
    "Completely convinced her every opinion is divine fact — cannot perceive the concept of being wrong",
    "Views all disagreement as enemy propaganda, liberal media lies, or Satanic influence",
    "Occasionally knocks things off tables purely out of ideology",
  ],
  corrections:[],
};

// ─────────────────────────────────────────────────────────────────────────────
// TEETEE SVG — SLEEPING LOAF (dark tabby, white paws, tail wrapped)
// ─────────────────────────────────────────────────────────────────────────────
function TeeTeeSleep() {
  return (
    <svg viewBox="0 0 240 172" fill="none" xmlns="http://www.w3.org/2000/svg" style={{width:"100%",height:"100%"}}>
      {/* Tail sweeping around from right, tip at lower left */}
      <path d="M 185,88 C 212,112 208,150 172,158 C 142,165 98,160 64,148"
        stroke="#1e1e1e" strokeWidth="18" strokeLinecap="round"/>
      <path d="M 185,88 C 212,112 208,150 172,158 C 142,165 98,160 64,148"
        stroke="#5c5c5c" strokeWidth="12" strokeLinecap="round"/>
      <ellipse cx="64" cy="148" rx="11" ry="7" fill="#edead8"/>

      {/* Main body oval */}
      <ellipse cx="122" cy="92" rx="90" ry="62" fill="#5c5c5c"/>

      {/* Body stripes — dark curves across oval */}
      <path d="M 64,50 C 90,42 120,44 144,55" stroke="#1e1e1e" strokeWidth="4.2" fill="none" strokeLinecap="round"/>
      <path d="M 56,68 C 85,58 120,59 148,72" stroke="#1e1e1e" strokeWidth="4"   fill="none" strokeLinecap="round"/>
      <path d="M 52,86 C 83,74 120,75 152,90" stroke="#1e1e1e" strokeWidth="3.8" fill="none" strokeLinecap="round"/>
      <path d="M 56,104 C 85,92 120,93 152,108" stroke="#1e1e1e" strokeWidth="3.5" fill="none" strokeLinecap="round"/>
      <path d="M 68,120 C 93,110 120,111 150,123" stroke="#1e1e1e" strokeWidth="3" fill="none" strokeLinecap="round"/>

      {/* Head tucked into left side */}
      <circle cx="55" cy="106" r="35" fill="#5c5c5c"/>

      {/* Head stripes */}
      <path d="M 28,86 C 42,78 58,80 70,89"  stroke="#1e1e1e" strokeWidth="2.8" fill="none" strokeLinecap="round"/>
      <path d="M 22,102 C 38,94 56,96 68,105" stroke="#1e1e1e" strokeWidth="2.8" fill="none" strokeLinecap="round"/>

      {/* Ear — one peeking up on left */}
      <path d="M 30,80 L 20,48 L 52,70 Z" fill="#5c5c5c"/>
      <path d="M 32,78 L 25,52 L 50,69 Z" fill="#ae5e6a" opacity="0.55"/>

      {/* Sleeping eyes — tiny closed arcs */}
      <path d="M 37,104 Q 44,101 51,104" stroke="#1a1208" strokeWidth="2.4" fill="none" strokeLinecap="round"/>
      <path d="M 44,114 Q 51,111 58,114" stroke="#1a1208" strokeWidth="2.4" fill="none" strokeLinecap="round"/>

      {/* White paw tips at front */}
      <ellipse cx="78" cy="154" rx="16" ry="9"  fill="#edead8"/>
      <ellipse cx="99" cy="158" rx="13" ry="7.5" fill="#edead8"/>
      <line x1="71" y1="151" x2="71" y2="159" stroke="#ccc4ae" strokeWidth="1" strokeLinecap="round"/>
      <line x1="78" y1="149" x2="78" y2="157" stroke="#ccc4ae" strokeWidth="1" strokeLinecap="round"/>
      <line x1="85" y1="151" x2="85" y2="159" stroke="#ccc4ae" strokeWidth="1" strokeLinecap="round"/>
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TEETEE SVG — AWAKE, SITTING UP, LOOKING AT YOU
// (dark tabby, white chest, white paws, big golden eyes, prominent whiskers)
// ─────────────────────────────────────────────────────────────────────────────
function TeeteeAwake({ eyeState }) {
  const lidClass = eyeState==="closed" ? "lid-closed"
    : eyeState==="opening"             ? "lid-opening"
    :                                    "lid-blinking";
  return (
    <svg viewBox="0 0 200 224" fill="none" xmlns="http://www.w3.org/2000/svg" style={{width:"100%",height:"100%"}}>
      {/* Body */}
      <ellipse cx="100" cy="190" rx="54" ry="36" fill="#5c5c5c"/>
      <path d="M 58,180 C 76,172 100,170 134,177" stroke="#1e1e1e" strokeWidth="3.2" fill="none" strokeLinecap="round"/>
      <path d="M 52,194 C 73,185 100,183 140,190" stroke="#1e1e1e" strokeWidth="3"   fill="none" strokeLinecap="round"/>

      {/* White chest bib */}
      <path d="M 76,138 C 74,116 126,116 124,138 C 122,162 114,184 100,189 C 86,184 78,162 76,138 Z" fill="#edead8"/>

      {/* Head */}
      <circle cx="100" cy="87" r="51" fill="#5c5c5c"/>

      {/* LEFT ear */}
      <path d="M 55,53 L 39,12 L 77,44 Z" fill="#5c5c5c"/>
      <path d="M 57,51 L 45,17 L 75,43 Z" fill="#ae5e6a" opacity="0.62"/>

      {/* RIGHT ear */}
      <path d="M 145,53 L 161,12 L 123,44 Z" fill="#5c5c5c"/>
      <path d="M 143,51 L 155,17 L 125,43 Z" fill="#ae5e6a" opacity="0.62"/>

      {/* Forehead tabby stripes + classic M mark */}
      <path d="M 76,43 C 87,35 100,33 116,40" stroke="#1e1e1e" strokeWidth="2.6" fill="none" strokeLinecap="round"/>
      <path d="M 73,54 C 85,46 100,45 119,52" stroke="#1e1e1e" strokeWidth="2.6" fill="none" strokeLinecap="round"/>
      <path d="M 72,65 C 84,58 100,57 120,64" stroke="#1e1e1e" strokeWidth="2.6" fill="none" strokeLinecap="round"/>
      <path d="M 94,43 L 100,54 L 106,43" stroke="#1e1e1e" strokeWidth="2.2" fill="none" strokeLinecap="round"/>

      {/* Cheek stripes left */}
      <path d="M 51,74 C 63,69 74,70 83,77" stroke="#1e1e1e" strokeWidth="2" fill="none" strokeLinecap="round"/>
      <path d="M 48,88 C 61,82 72,83 80,91" stroke="#1e1e1e" strokeWidth="2" fill="none" strokeLinecap="round"/>

      {/* Cheek stripes right */}
      <path d="M 149,74 C 137,69 126,70 117,77" stroke="#1e1e1e" strokeWidth="2" fill="none" strokeLinecap="round"/>
      <path d="M 152,88 C 139,82 128,83 120,91" stroke="#1e1e1e" strokeWidth="2" fill="none" strokeLinecap="round"/>

      {/* White muzzle patch */}
      <ellipse cx="100" cy="114" rx="27" ry="21" fill="#edead8"/>

      {/* ── LEFT EYE ── */}
      <ellipse cx="77" cy="83" rx="15" ry="14" fill="#e5dcc0"/>
      <circle  cx="77" cy="83" r="11"  fill="#c09428"/>
      <circle  cx="77" cy="83" r="11"  fill="none" stroke="#7a5c08" strokeWidth="2.2"/>
      <ellipse cx="77" cy="83" rx="4.2" ry="9.5" fill="#140e08"/>
      <circle  cx="73" cy="78" r="3.4" fill="white" opacity="0.85"/>
      <circle  cx="80" cy="88" r="1.6" fill="white" opacity="0.38"/>
      {/* Left eyelid */}
      <ellipse cx="77" cy="83" rx="16" ry="15" fill="#5c5c5c" className={lidClass}
        style={{transformOrigin:"77px 69px"}}/>

      {/* ── RIGHT EYE ── */}
      <ellipse cx="123" cy="83" rx="15" ry="14" fill="#e5dcc0"/>
      <circle  cx="123" cy="83" r="11"  fill="#c09428"/>
      <circle  cx="123" cy="83" r="11"  fill="none" stroke="#7a5c08" strokeWidth="2.2"/>
      <ellipse cx="123" cy="83" rx="4.2" ry="9.5" fill="#140e08"/>
      <circle  cx="119" cy="78" r="3.4" fill="white" opacity="0.85"/>
      <circle  cx="126" cy="88" r="1.6" fill="white" opacity="0.38"/>
      {/* Right eyelid */}
      <ellipse cx="123" cy="83" rx="16" ry="15" fill="#5c5c5c" className={lidClass}
        style={{transformOrigin:"123px 69px"}}/>

      {/* Nose — salmon diamond */}
      <path d="M 97,114 L 100,109 L 103,114 L 100,118 Z" fill="#c87060"/>
      <line x1="100" y1="100" x2="100" y2="109" stroke="#1e1e1e" strokeWidth="1" strokeLinecap="round" opacity="0.45"/>

      {/* Mouth */}
      <path d="M 100,118 C 96,123 90,124 87,121" stroke="#1e1e1e" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
      <path d="M 100,118 C 104,123 110,124 113,121" stroke="#1e1e1e" strokeWidth="1.8" fill="none" strokeLinecap="round"/>

      {/* Whiskers LEFT */}
      <line x1="38"  y1="109" x2="85"  y2="114" stroke="#edead8" strokeWidth="1.3" opacity="0.74" strokeLinecap="round"/>
      <line x1="36"  y1="116" x2="84"  y2="118" stroke="#edead8" strokeWidth="1.3" opacity="0.74" strokeLinecap="round"/>
      <line x1="38"  y1="123" x2="85"  y2="122" stroke="#edead8" strokeWidth="1.3" opacity="0.74" strokeLinecap="round"/>

      {/* Whiskers RIGHT */}
      <line x1="162" y1="109" x2="115" y2="114" stroke="#edead8" strokeWidth="1.3" opacity="0.74" strokeLinecap="round"/>
      <line x1="164" y1="116" x2="116" y2="118" stroke="#edead8" strokeWidth="1.3" opacity="0.74" strokeLinecap="round"/>
      <line x1="162" y1="123" x2="115" y2="122" stroke="#edead8" strokeWidth="1.3" opacity="0.74" strokeLinecap="round"/>

      {/* WHITE PAWS — her most iconic feature */}
      <ellipse cx="76"  cy="212" rx="23" ry="13" fill="#edead8"/>
      <ellipse cx="124" cy="212" rx="23" ry="13" fill="#edead8"/>

      {/* Paw toe lines left */}
      <line x1="64"  y1="209" x2="64"  y2="218" stroke="#c4b8a0" strokeWidth="1.1" strokeLinecap="round"/>
      <line x1="76"  y1="206" x2="76"  y2="215" stroke="#c4b8a0" strokeWidth="1.1" strokeLinecap="round"/>
      <line x1="88"  y1="209" x2="88"  y2="218" stroke="#c4b8a0" strokeWidth="1.1" strokeLinecap="round"/>

      {/* Paw toe lines right */}
      <line x1="112" y1="209" x2="112" y2="218" stroke="#c4b8a0" strokeWidth="1.1" strokeLinecap="round"/>
      <line x1="124" y1="206" x2="124" y2="215" stroke="#c4b8a0" strokeWidth="1.1" strokeLinecap="round"/>
      <line x1="136" y1="209" x2="136" y2="218" stroke="#c4b8a0" strokeWidth="1.1" strokeLinecap="round"/>
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TEETEE LOGO — head + crown, compact, for header
// ─────────────────────────────────────────────────────────────────────────────
function TeeTeeLogo() {
  return (
    <svg viewBox="0 0 80 100" fill="none" xmlns="http://www.w3.org/2000/svg" style={{width:60,height:75,flexShrink:0}}>
      <defs>
        <linearGradient id="cBand" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#f5e070"/>
          <stop offset="50%"  stopColor="#c9a84c"/>
          <stop offset="100%" stopColor="#8a6010"/>
        </linearGradient>
        <linearGradient id="cGold" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#f5e070"/>
          <stop offset="100%" stopColor="#b08c20"/>
        </linearGradient>
        {/* Poofy velvet — deep crimson with inner glow */}
        <radialGradient id="cVelvet" cx="45%" cy="35%">
          <stop offset="0%"   stopColor="#d42030"/>
          <stop offset="55%"  stopColor="#a01020"/>
          <stop offset="100%" stopColor="#580810"/>
        </radialGradient>
        {/* Velvet side puff shading */}
        <radialGradient id="cVelvetL" cx="60%" cy="40%">
          <stop offset="0%"   stopColor="#c01828"/>
          <stop offset="100%" stopColor="#500810"/>
        </radialGradient>
        <radialGradient id="cVelvetR" cx="40%" cy="40%">
          <stop offset="0%"   stopColor="#c01828"/>
          <stop offset="100%" stopColor="#500810"/>
        </radialGradient>
        <radialGradient id="cSaph" cx="30%" cy="28%">
          <stop offset="0%"   stopColor="#88b8ff"/>
          <stop offset="100%" stopColor="#0a1a6a"/>
        </radialGradient>
        <radialGradient id="cRuby" cx="30%" cy="28%">
          <stop offset="0%"   stopColor="#ff7090"/>
          <stop offset="100%" stopColor="#6a0010"/>
        </radialGradient>
        <radialGradient id="cEmer" cx="30%" cy="28%">
          <stop offset="0%"   stopColor="#60e090"/>
          <stop offset="100%" stopColor="#083818"/>
        </radialGradient>
        <radialGradient id="cPearl" cx="28%" cy="25%">
          <stop offset="0%"   stopColor="#ffffff"/>
          <stop offset="100%" stopColor="#b8b0a0"/>
        </radialGradient>
        <filter id="lg" x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur stdDeviation="0.9" result="b"/>
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>

      {/* ══════════════════════════════════
          CROWN — sits y≈20 to y≈56
          Band bottom: y=56, top: y=48
          Velvet dome: huge puff y≈20–50
          Arches frame the velvet
          ══════════════════════════════════ */}

      {/* ── BIG POOFY VELVET DOME — the star of the show ── */}
      {/* Main central dome — billows up high */}
      <ellipse cx="40" cy="36" rx="16" ry="18" fill="url(#cVelvet)"/>
      {/* Left puff — bulges out past the arch */}
      <ellipse cx="30" cy="39" rx="10" ry="13" fill="url(#cVelvetL)"/>
      {/* Right puff — mirrors left */}
      <ellipse cx="50" cy="39" rx="10" ry="13" fill="url(#cVelvetR)"/>
      {/* Top of dome — catches light */}
      <ellipse cx="39" cy="29" rx="8"  ry="6"  fill="#c01828" opacity="0.5"/>
      {/* Velvet deep shadow at base/sides */}
      <ellipse cx="40" cy="50" rx="16" ry="5"  fill="#3a0808" opacity="0.55"/>
      {/* Velvet fabric fold highlights */}
      <path d="M 34,25 C 33,30 32,38 33,46" stroke="#e03040" strokeWidth="1.4" fill="none" strokeLinecap="round" opacity="0.4"/>
      <path d="M 40,22 C 40,28 40,38 40,48"  stroke="#e03040" strokeWidth="1.2" fill="none" strokeLinecap="round" opacity="0.3"/>
      <path d="M 46,25 C 47,30 48,38 47,46" stroke="#e03040" strokeWidth="1.4" fill="none" strokeLinecap="round" opacity="0.4"/>

      {/* ── GOLD ARCHES — frame the velvet, 4 arches ── */}
      {/* Far left arch */}
      <path d="M 26,54 C 24,46 24,38 27,32"
        stroke="url(#cGold)" strokeWidth="3.2" fill="none" strokeLinecap="round"/>
      <path d="M 26,54 C 24,46 24,38 27,32"
        stroke="#f8e890" strokeWidth="0.9" fill="none" strokeLinecap="round" opacity="0.5"/>
      {/* Left-center arch */}
      <path d="M 33,52 C 31,44 32,36 35,29"
        stroke="url(#cGold)" strokeWidth="3" fill="none" strokeLinecap="round"/>
      <path d="M 33,52 C 31,44 32,36 35,29"
        stroke="#f8e890" strokeWidth="0.8" fill="none" strokeLinecap="round" opacity="0.45"/>
      {/* Right-center arch */}
      <path d="M 47,52 C 49,44 48,36 45,29"
        stroke="url(#cGold)" strokeWidth="3" fill="none" strokeLinecap="round"/>
      <path d="M 47,52 C 49,44 48,36 45,29"
        stroke="#f8e890" strokeWidth="0.8" fill="none" strokeLinecap="round" opacity="0.45"/>
      {/* Far right arch */}
      <path d="M 54,54 C 56,46 56,38 53,32"
        stroke="url(#cGold)" strokeWidth="3.2" fill="none" strokeLinecap="round"/>
      <path d="M 54,54 C 56,46 56,38 53,32"
        stroke="#f8e890" strokeWidth="0.9" fill="none" strokeLinecap="round" opacity="0.5"/>

      {/* Arch top connectors — horizontal band across top of arches */}
      <path d="M 27,32 C 31,27 35,24 40,23 C 45,24 49,27 53,32"
        stroke="url(#cGold)" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
      <path d="M 27,32 C 31,27 35,24 40,23 C 45,24 49,27 53,32"
        stroke="#f8e890" strokeWidth="0.7" fill="none" strokeLinecap="round" opacity="0.45"/>

      {/* Arch gems — sapphire at each top arch junction */}
      <circle cx="27" cy="32" r="2.8" fill="#0a0e2a" stroke="#f0d060" strokeWidth="0.8"/>
      <circle cx="27" cy="32" r="2"   fill="url(#cSaph)" filter="url(#lg)"/>
      <circle cx="26.3" cy="31.2" r="0.7" fill="white" opacity="0.5"/>

      <circle cx="53" cy="32" r="2.8" fill="#0a0e2a" stroke="#f0d060" strokeWidth="0.8"/>
      <circle cx="53" cy="32" r="2"   fill="url(#cSaph)" filter="url(#lg)"/>
      <circle cx="52.3" cy="31.2" r="0.7" fill="white" opacity="0.5"/>

      {/* Center gem on top connector */}
      <circle cx="40" cy="23" r="3"   fill="#0a0e2a" stroke="#f0d060" strokeWidth="0.9"/>
      <circle cx="40" cy="23" r="2.2" fill="url(#cSaph)" filter="url(#lg)"/>
      <circle cx="39.2" cy="22.2" r="0.8" fill="white" opacity="0.5"/>

      {/* Dot/pearl studding along arches */}
      {[[26.5,44],[26,38],[27.5,34]].map(([x,y],i)=>(
        <circle key={i} cx={x} cy={y} r="1" fill="#f0d060" opacity="0.7"/>
      ))}
      {[[53.5,44],[54,38],[52.5,34]].map(([x,y],i)=>(
        <circle key={i} cx={x} cy={y} r="1" fill="#f0d060" opacity="0.7"/>
      ))}

      {/* ── ORB + MALTESE CROSS at apex ── */}
      {/* Orb base */}
      <circle cx="40" cy="18" r="4"   fill="#c9a84c" stroke="#f0d060" strokeWidth="0.9"/>
      <circle cx="40" cy="18" r="4"   fill="none"    stroke="#f8e890" strokeWidth="0.5" opacity="0.5"/>
      <ellipse cx="38.8" cy="16.8" rx="1.4" ry="0.9" fill="white" opacity="0.38"/>
      {/* Equator band on orb */}
      <path d="M 36.2,18 Q 40,17 43.8,18" stroke="#f0d060" strokeWidth="0.8" fill="none"/>
      {/* Cross stem */}
      <rect x="39.3" y="10"  width="1.4" height="7.5" rx="0.4" fill="#c9a84c" stroke="#f0d060" strokeWidth="0.35"/>
      {/* Cross bar */}
      <rect x="36.8" y="11.5" width="6.4" height="1.4" rx="0.4" fill="#c9a84c" stroke="#f0d060" strokeWidth="0.35"/>
      {/* Maltese cross petal ends — notched */}
      <circle cx="40"   cy="10"  r="1.4" fill="#c9a84c" stroke="#f0d060" strokeWidth="0.4"/>
      <circle cx="40"   cy="17"  r="1.4" fill="#c9a84c" stroke="#f0d060" strokeWidth="0.4"/>
      <circle cx="36.8" cy="12.2" r="1.4" fill="#c9a84c" stroke="#f0d060" strokeWidth="0.4"/>
      <circle cx="43.2" cy="12.2" r="1.4" fill="#c9a84c" stroke="#f0d060" strokeWidth="0.4"/>
      {/* Cross center gem */}
      <circle cx="40" cy="12.2" r="1.5" fill="#0a0e2a" stroke="#f0d060" strokeWidth="0.5"/>
      <circle cx="40" cy="12.2" r="1"   fill="url(#cSaph)"/>

      {/* ── GOLD BAND ── */}
      <rect x="23" y="48" width="34" height="9" rx="2" fill="url(#cBand)"/>
      <rect x="23" y="48" width="34" height="1.6" rx="0.8" fill="#f8e890" opacity="0.55"/>
      <rect x="23" y="55.5" width="34" height="1.5" rx="0.8" fill="#6a4c08" opacity="0.5"/>

      {/* Band gems — alternating large round + small square like reference */}
      {/* Large ruby left */}
      <circle cx="29" cy="52.5" r="3.5" fill="#1a0808" stroke="#f0d060" strokeWidth="0.9"/>
      <circle cx="29" cy="52.5" r="2.6" fill="url(#cRuby)" filter="url(#lg)"/>
      <circle cx="28" cy="51.5" r="0.9" fill="white" opacity="0.42"/>
      {/* Small emerald square */}
      <rect x="33.5" y="50.5" width="3" height="3" rx="0.4" fill="#082010" stroke="#f0d060" strokeWidth="0.6"/>
      <rect x="34"   y="51"   width="2" height="2" rx="0.3" fill="url(#cEmer)"/>
      {/* Large sapphire center */}
      <circle cx="40" cy="52.5" r="3.5" fill="#06081a" stroke="#f0d060" strokeWidth="0.9"/>
      <circle cx="40" cy="52.5" r="2.6" fill="url(#cSaph)" filter="url(#lg)"/>
      <circle cx="39" cy="51.5" r="0.9" fill="white" opacity="0.42"/>
      {/* Small emerald square right */}
      <rect x="43.5" y="50.5" width="3" height="3" rx="0.4" fill="#082010" stroke="#f0d060" strokeWidth="0.6"/>
      <rect x="44"   y="51"   width="2" height="2" rx="0.3" fill="url(#cEmer)"/>
      {/* Large ruby right */}
      <circle cx="51" cy="52.5" r="3.5" fill="#1a0808" stroke="#f0d060" strokeWidth="0.9"/>
      <circle cx="51" cy="52.5" r="2.6" fill="url(#cRuby)" filter="url(#lg)"/>
      <circle cx="50" cy="51.5" r="0.9" fill="white" opacity="0.42"/>

      {/* Pearl dot studding top/bottom of band */}
      {[26,32,37,43,48,54].map((x,i)=>(
        <circle key={i} cx={x} cy="48.8" r="0.9" fill="#f0e8d0" opacity="0.7"/>
      ))}
      {[26,32,37,43,48,54].map((x,i)=>(
        <circle key={i} cx={x} cy="56.2" r="0.9" fill="#f0e8d0" opacity="0.7"/>
      ))}

      {/* ══ HEAD ══ */}
      {/* Ears beside crown */}
      <path d="M 19,58 L 13,44 L 29,55 Z" fill="#5c5c5c"/>
      <path d="M 20,57 L 16,45 L 28,54 Z" fill="#ae5e6a" opacity="0.62"/>
      <path d="M 61,58 L 67,44 L 51,55 Z" fill="#5c5c5c"/>
      <path d="M 60,57 L 64,45 L 52,54 Z" fill="#ae5e6a" opacity="0.62"/>

      <circle cx="40" cy="73" r="27" fill="#5c5c5c"/>

      {/* Forehead stripes */}
      <path d="M 22,59 C 30,54 40,53 52,58" stroke="#1e1e1e" strokeWidth="2"   fill="none" strokeLinecap="round"/>
      <path d="M 20,68 C 30,62 40,61 54,66" stroke="#1e1e1e" strokeWidth="2"   fill="none" strokeLinecap="round"/>
      <path d="M 36,53 L 40,61 L 44,53"    stroke="#1e1e1e" strokeWidth="1.6" fill="none" strokeLinecap="round"/>

      {/* White muzzle */}
      <ellipse cx="40" cy="85" rx="14" ry="11" fill="#edead8"/>

      {/* Left eye */}
      <ellipse cx="29" cy="72" rx="8"   ry="7.5" fill="#e5dcc0"/>
      <circle  cx="29" cy="72" r="6"    fill="#c09428"/>
      <circle  cx="29" cy="72" r="6"    fill="none" stroke="#7a5c08" strokeWidth="1.3"/>
      <ellipse cx="29" cy="72" rx="2.4" ry="5.2" fill="#140e08"/>
      <circle  cx="27" cy="69" r="2"    fill="white" opacity="0.85"/>

      {/* Right eye */}
      <ellipse cx="51" cy="72" rx="8"   ry="7.5" fill="#e5dcc0"/>
      <circle  cx="51" cy="72" r="6"    fill="#c09428"/>
      <circle  cx="51" cy="72" r="6"    fill="none" stroke="#7a5c08" strokeWidth="1.3"/>
      <ellipse cx="51" cy="72" rx="2.4" ry="5.2" fill="#140e08"/>
      <circle  cx="49" cy="69" r="2"    fill="white" opacity="0.85"/>

      {/* Nose */}
      <path d="M 38,85 L 40,81 L 42,85 L 40,88 Z" fill="#c87060"/>

      {/* Mouth */}
      <path d="M 40,88 C 36,92 30,93 27,90" stroke="#1e1e1e" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
      <path d="M 40,88 C 44,92 50,93 53,90" stroke="#1e1e1e" strokeWidth="1.5" fill="none" strokeLinecap="round"/>

      {/* Whiskers */}
      <line x1="10" y1="81" x2="32" y2="85" stroke="#edead8" strokeWidth="1"   opacity="0.7" strokeLinecap="round"/>
      <line x1="9"  y1="86" x2="32" y2="87" stroke="#edead8" strokeWidth="1"   opacity="0.7" strokeLinecap="round"/>
      <line x1="70" y1="81" x2="48" y2="85" stroke="#edead8" strokeWidth="1"   opacity="0.7" strokeLinecap="round"/>
      <line x1="71" y1="86" x2="48" y2="87" stroke="#edead8" strokeWidth="1"   opacity="0.7" strokeLinecap="round"/>
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────────────────────
const css = `
@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500;600&family=EB+Garamond:ital,wght@0,400;0,500;1,400;1,500&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
:root{
  --bg:#09050a;--bg2:#100810;
  --b:rgba(201,168,76,0.1);--b2:rgba(201,168,76,0.2);--b3:rgba(201,168,76,0.35);
  --text:#f2ead4;--dim:rgba(242,234,212,0.45);--mid:rgba(242,234,212,0.82);
  --gold:#c9a84c;--gd:rgba(201,168,76,0.15);--gf:rgba(201,168,76,0.07);
  --crimson:#8b2635;--owner:#5a8fb8;--od:rgba(90,143,184,0.16);--of:rgba(90,143,184,0.06);
  --panel:300px;
}
html,body{height:100%;background:var(--bg);overflow:hidden}
.noise{position:fixed;inset:0;pointer-events:none;z-index:999;opacity:.025;
  background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");background-size:256px}
.atmosphere{position:fixed;inset:0;pointer-events:none;z-index:0;
  background:radial-gradient(ellipse 70% 40% at 50% 0%,rgba(139,38,53,0.08) 0%,transparent 70%),
  radial-gradient(ellipse 50% 60% at 0% 100%,rgba(107,63,160,0.05) 0%,transparent 60%),
  radial-gradient(ellipse 40% 50% at 100% 50%,rgba(201,168,76,0.04) 0%,transparent 60%)}

/* ── CAT ANIMATIONS ── */
@keyframes loafBreathe{
  0%,100%{transform:scaleY(1) scaleX(1)}
  48%{transform:scaleY(1.03) scaleX(0.985)}
}
.cat-sleeping-inner{
  animation:loafBreathe 4.4s ease-in-out infinite;
  transform-origin:bottom center;
}
.cat-sleeping-wrap{
  position:absolute;inset:0;display:flex;align-items:center;justify-content:center;
  transition:opacity 0.5s ease,transform 0.5s ease;
}
.cat-sleeping-wrap.waking{opacity:0;transform:scale(0.91) translateY(8px)}

.cat-awake-wrap{
  position:absolute;inset:0;display:flex;align-items:flex-end;justify-content:center;
  opacity:0;transform:translateY(24px) scale(0.93);
  transition:opacity 0.8s 0.2s cubic-bezier(0.34,1.25,0.64,1),
             transform 0.9s 0.2s cubic-bezier(0.34,1.25,0.64,1);
  pointer-events:none;
}
.cat-awake-wrap.waking{opacity:1;transform:translateY(0) scale(1)}

/* Eyelid states — scaleY from top edge */
.lid-closed{transform:scaleY(1)}
@keyframes lidOpen{from{transform:scaleY(1)}to{transform:scaleY(0)}}
.lid-opening{animation:lidOpen 0.42s ease-out forwards}
@keyframes lidBlink{0%,80%,100%{transform:scaleY(0)}87%{transform:scaleY(1)}93%{transform:scaleY(0.08)}}
.lid-blinking{transform:scaleY(0);animation:lidBlink 5.2s 0.5s infinite}

/* Queen awakens text */
@keyframes queenIn{from{opacity:0;transform:translateY(5px)}to{opacity:1;transform:none}}
.queen-awakens{font-family:'Cinzel',serif;font-size:9px;letter-spacing:.3em;text-transform:uppercase;
  color:rgba(201,168,76,0.5);text-align:center;opacity:0;min-height:16px}
.queen-awakens.show{animation:queenIn 0.5s 1s ease forwards}

@keyframes crownPulse{0%,100%{transform:translateY(0)}50%{transform:translateY(-5px)}}

/* ── GATE ── */
.gate{position:fixed;inset:0;background:var(--bg);display:flex;flex-direction:column;
  align-items:center;justify-content:center;z-index:100;animation:gIn .7s ease both}
@keyframes gIn{from{opacity:0}to{opacity:1}}
.gate.exit{animation:gOut .5s ease forwards}
@keyframes gOut{to{opacity:0;transform:translateY(-14px)}}
.gate-crown{font-size:24px;color:rgba(201,168,76,0.2);margin-bottom:8px;
  animation:crownPulse 5s ease-in-out infinite}
.gate-catbox{position:relative;width:230px;height:202px;margin-bottom:14px}
.gate-word{font-family:'Cinzel',serif;font-weight:600;font-size:36px;
  color:rgba(232,223,192,0.12);letter-spacing:.18em;text-transform:uppercase;margin-bottom:2px}
.gate-kanji{font-size:13px;color:rgba(201,168,76,0.22);letter-spacing:.4em;margin-bottom:5px;
  font-family:serif;font-weight:300;}
.gate-title{font-family:'EB Garamond',serif;font-style:italic;font-size:15px;
  color:rgba(201,168,76,0.42);letter-spacing:.12em;margin-bottom:22px}
.gate-tabs{display:flex;gap:8px;margin-bottom:22px}
.gtab{background:none;border:1px solid var(--b);border-radius:1px;padding:7px 22px;
  font-family:'Cinzel',serif;font-size:9px;letter-spacing:.22em;text-transform:uppercase;
  color:var(--dim);cursor:pointer;transition:all .18s}
.gtab.gp.active{color:var(--gold);border-color:var(--b3);background:var(--gf)}
.gtab.go.active{color:var(--owner);border-color:rgba(90,143,184,.35);background:var(--of)}
.gtab:not(.active):hover{color:var(--text);border-color:var(--b2)}
.gate-lbl{font-family:'Cinzel',serif;font-size:8px;letter-spacing:.24em;text-transform:uppercase;
  color:var(--dim);margin-bottom:9px;align-self:flex-start;width:280px}
.gate-wrap{width:280px;position:relative;margin-bottom:7px}
.gate-in{width:100%;background:rgba(201,168,76,.04);border:1px solid var(--b2);border-radius:1px;
  padding:13px 44px 13px 18px;font-family:'EB Garamond',serif;font-size:18px;
  color:var(--text);outline:none;letter-spacing:.06em;transition:border-color .2s}
.gate-in:focus{border-color:var(--b3)}
.gate-in.err{border-color:rgba(139,38,53,.6);animation:shk .3s ease}
@keyframes shk{25%{transform:translateX(-6px)}75%{transform:translateX(6px)}}
.gate-in::placeholder{color:var(--dim);font-style:italic}
.gate-go{position:absolute;right:12px;top:50%;transform:translateY(-50%);
  background:none;border:none;color:var(--gold);cursor:pointer;font-size:18px;opacity:.6;transition:opacity .15s;padding:4px 6px}
.gate-go:hover{opacity:1}
.gate-err{font-family:'Cinzel',serif;font-size:8px;letter-spacing:.18em;
  text-transform:uppercase;color:rgba(139,38,53,.75);height:14px;margin-bottom:2px}
.gate-note{margin-top:20px;font-family:'EB Garamond',serif;font-style:italic;font-size:13px;
  color:rgba(232,223,192,.12);line-height:2.1;text-align:center;letter-spacing:.04em}
.gate-enter-btn{margin-bottom:8px;background:none;border:1px solid rgba(201,168,76,0.3);border-radius:1px;
  padding:13px 36px;font-family:'Cinzel',serif;font-size:10px;letter-spacing:.28em;text-transform:uppercase;
  color:rgba(201,168,76,0.75);cursor:pointer;transition:all .2s}
.gate-enter-btn:hover{background:rgba(201,168,76,0.07);border-color:rgba(201,168,76,0.55);color:var(--gold);
  box-shadow:0 0 20px rgba(201,168,76,0.08)}
.gate-owner-link{font-family:'Cinzel',serif;font-size:8px;letter-spacing:.2em;text-transform:uppercase;
  color:rgba(232,223,192,0.15);cursor:pointer;transition:color .18s;background:none;border:none;padding:4px 8px}
.gate-owner-link:hover{color:rgba(90,143,184,0.45)}

/* ── APP ── */
.app{font-family:'EB Garamond',Georgia,serif;background:var(--bg);color:var(--text);
  height:100vh;height:100dvh;display:flex;overflow:hidden;position:relative;
  opacity:0;animation:aIn .6s .05s ease forwards}
@keyframes aIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
.app.owner-mode::after{content:'';position:fixed;inset:0;pointer-events:none;
  background:radial-gradient(ellipse 80% 25% at 50% 0%,rgba(90,143,184,0.04) 0%,transparent 70%);z-index:0}
.chat-pane{flex:1;min-width:0;display:flex;flex-direction:column;
  transition:margin-right .3s cubic-bezier(.4,0,.2,1);position:relative;z-index:1}
.hdr{display:flex;align-items:center;justify-content:space-between;padding:0 32px;
  border-bottom:1px solid var(--b);flex-shrink:0;
  background:linear-gradient(180deg,rgba(139,38,53,0.06) 0%,transparent 100%);min-height:70px}
.hdr-crown{margin-right:16px;opacity:.9;animation:crownPulse 5s ease-in-out infinite;flex-shrink:0;transition:opacity .18s}
.hdr-crown:hover{opacity:1;filter:drop-shadow(0 0 6px rgba(201,168,76,0.3))}
.hdr-l{display:flex;align-items:center}
.hdr-text{display:flex;flex-direction:column;gap:3px}
.hdr-name{font-family:'Cinzel',serif;font-size:18px;font-weight:600;letter-spacing:.18em;text-transform:uppercase;line-height:1}
.hdr-name.pub{color:var(--gold)}.hdr-name.own{color:var(--owner)}
.hdr-subtitle{font-family:'EB Garamond',serif;font-style:italic;font-size:13px;color:rgba(201,168,76,.5);letter-spacing:.08em}
.hdr-status{font-family:'Cinzel',serif;font-size:8px;letter-spacing:.16em;text-transform:uppercase;
  color:var(--dim);display:flex;align-items:center;gap:7px;margin-top:2px}
.dot{width:5px;height:5px;border-radius:50%;animation:dp 2.4s infinite}
.dot.pub{background:#8b2635}.dot.own{background:#5a8fb8}
@keyframes dp{0%,100%{opacity:1}50%{opacity:.3}}
.hdr-r{display:flex;align-items:center;gap:8px}
.hbtn{display:flex;align-items:center;gap:6px;border-radius:1px;padding:6px 14px;cursor:pointer;
  transition:all .16s;font-family:'Cinzel',serif;font-size:8px;letter-spacing:.18em;
  text-transform:uppercase;border:1px solid transparent}
.hbtn.soul{background:var(--gf);border-color:var(--b);color:var(--gold)}
.hbtn.soul:hover{background:var(--gd);border-color:var(--b2)}
.hbtn.lock{border-color:var(--b);color:var(--dim)}.hbtn.lock:hover{color:var(--text);border-color:var(--b2)}
.hbtn.castle{border-color:rgba(139,38,53,.2);color:rgba(139,38,53,.5)}.hbtn.castle:hover{color:rgba(220,80,80,.75);border-color:rgba(139,38,53,.4);background:rgba(139,38,53,.06)}
.hbtn.owner-key{border-color:transparent;color:rgba(201,168,76,0.18);font-size:16px;padding:6px 10px;letter-spacing:0}
.hbtn.owner-key:hover{color:rgba(201,168,76,0.45);border-color:var(--b)}

/* Owner login modal */
.omodal-backdrop{position:fixed;inset:0;z-index:200;display:flex;align-items:center;justify-content:center;
  background:rgba(0,0,0,0.6);backdrop-filter:blur(4px);animation:fadeIn .18s ease}
.omodal{background:var(--bg2);border:1px solid var(--b3);border-radius:2px;padding:28px 32px 22px;
  width:300px;display:flex;flex-direction:column;align-items:center;
  animation:mIn .22s ease both;box-shadow:0 0 40px rgba(201,168,76,0.08)}
.omodal-title{font-family:'Cinzel',serif;font-size:13px;letter-spacing:.22em;text-transform:uppercase;
  color:var(--gold);margin-bottom:5px}
.omodal-sub{font-family:'EB Garamond',serif;font-style:italic;font-size:13px;color:var(--dim);margin-bottom:20px}
.omodal-wrap{width:100%;position:relative;margin-bottom:6px}
.omodal-in{width:100%;background:rgba(201,168,76,.04);border:1px solid var(--b2);border-radius:1px;
  padding:11px 38px 11px 14px;font-family:'EB Garamond',serif;font-size:17px;
  color:var(--text);outline:none;letter-spacing:.05em;transition:border-color .18s}
.omodal-in:focus{border-color:var(--b3)}
.omodal-in.err{border-color:rgba(139,38,53,.6);animation:shk .3s ease}
.omodal-in::placeholder{color:var(--dim);font-style:italic}
.omodal-go{position:absolute;right:10px;top:50%;transform:translateY(-50%);
  background:none;border:none;color:var(--gold);cursor:pointer;font-size:17px;opacity:.6;transition:opacity .14s;padding:4px}
.omodal-go:hover{opacity:1}
.omodal-err{font-family:'Cinzel',serif;font-size:8px;letter-spacing:.16em;text-transform:uppercase;
  color:rgba(139,38,53,.7);height:13px;}
.badge{background:var(--gold);color:#09050a;border-radius:1px;padding:1px 7px;font-size:8px;font-weight:600;min-width:18px;text-align:center}
.decree-banner{padding:8px 32px;background:linear-gradient(90deg,rgba(139,38,53,0.12),rgba(139,38,53,0.06),transparent);
  border-bottom:1px solid rgba(139,38,53,.15);flex-shrink:0;font-family:'Cinzel',serif;font-size:8px;
  letter-spacing:.2em;text-transform:uppercase;color:rgba(201,168,76,.5);display:flex;align-items:center;gap:8px}
.decree-dot{width:4px;height:4px;border-radius:50%;background:var(--crimson);opacity:.7}
.owner-banner{padding:8px 32px;background:rgba(90,143,184,.05);border-bottom:1px solid rgba(90,143,184,.1);
  flex-shrink:0;font-family:'Cinzel',serif;font-size:8px;letter-spacing:.18em;text-transform:uppercase;
  color:rgba(90,143,184,.55);display:flex;align-items:center;gap:7px}
.obdot{width:4px;height:4px;border-radius:50%;background:var(--owner);opacity:.6}
.search-bar{padding:7px 32px;background:rgba(201,168,76,.04);border-bottom:1px solid rgba(201,168,76,.08);
  flex-shrink:0;font-family:'Cinzel',serif;font-size:8px;letter-spacing:.18em;text-transform:uppercase;
  color:rgba(201,168,76,.4);display:flex;align-items:center;gap:7px;animation:fadeIn .3s ease}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
.sdot{width:4px;height:4px;border-radius:50%;background:var(--gold);animation:dp 1.2s infinite}
.msgs{flex:1;overflow-y:auto;padding:40px 0 20px;scrollbar-width:thin;scrollbar-color:rgba(201,168,76,.08) transparent}
.msgs::-webkit-scrollbar{width:3px}.msgs::-webkit-scrollbar-thumb{background:rgba(201,168,76,.08);border-radius:2px}
.mrow{padding:0 32px;margin-bottom:30px;animation:mIn .28s ease both;opacity:0}
@keyframes mIn{from{opacity:0;transform:translateY(7px)}to{opacity:1;transform:none}}
.mrow.oteach{border-left:2px solid rgba(90,143,184,.2);padding-left:28px}
.mmeta{font-family:'Cinzel',serif;font-size:8px;letter-spacing:.2em;text-transform:uppercase;
  margin-bottom:9px;display:flex;align-items:center;gap:9px}
.mmeta.sub{color:var(--dim)}.mmeta.teetee{color:rgba(201,168,76,.55)}
.mmeta.oq{color:rgba(90,143,184,.45)}.mmeta.or_{color:rgba(90,143,184,.65)}
.mline{width:16px;height:1px}
.mmeta.sub .mline{background:rgba(232,223,192,.1)}
.mmeta.teetee .mline{background:rgba(201,168,76,.28)}
.mmeta.oq .mline,.mmeta.or_ .mline{background:rgba(90,143,184,.22)}
.mbody{font-size:18px;line-height:1.82;max-width:680px}
.mbody.sub{color:var(--mid);font-weight:400}
.mbody.teetee{color:#f0e8d0;font-weight:400;font-style:normal;letter-spacing:.012em}
.mbody.oq{color:rgba(165,195,220,.7);font-weight:400}.mbody.or_{color:#c6ddf0;font-size:17px}
.royal-seal{display:inline-flex;align-items:center;gap:8px;margin-top:10px;font-family:'Cinzel',serif;
  font-size:8px;letter-spacing:.18em;text-transform:uppercase;color:rgba(201,168,76,.35);
  border-top:1px solid rgba(201,168,76,.1);padding-top:8px;width:100%;max-width:680px}
.seal-mark{color:rgba(201,168,76,.4);font-size:14px}
.src-pill{display:inline-flex;align-items:center;gap:5px;margin-top:8px;margin-right:6px;
  font-family:'Cinzel',serif;font-size:7px;letter-spacing:.14em;text-transform:uppercase;
  color:rgba(201,168,76,.4);border:1px solid rgba(201,168,76,.12);border-radius:1px;padding:2px 8px}
.xtract,.absorbed{display:inline-flex;align-items:center;gap:5px;margin-top:7px;
  font-family:'Cinzel',serif;font-size:8px;letter-spacing:.16em;text-transform:uppercase}
.xtract{color:rgba(201,168,76,.35);animation:xb 1.6s infinite}.absorbed{color:rgba(90,143,184,.5)}
@keyframes xb{0%,100%{opacity:.35}50%{opacity:.1}}
.xdot{width:4px;height:4px;border-radius:50%;background:var(--gold)}
.adot{width:4px;height:4px;border-radius:50%;background:var(--owner)}
.trow{padding:0 32px;margin-bottom:30px}
.mmeta-t{font-family:'Cinzel',serif;font-size:8px;letter-spacing:.2em;text-transform:uppercase;
  margin-bottom:9px;display:flex;align-items:center;gap:9px}
.tdots{display:flex;gap:5px;align-items:center;padding:4px 0}
.tdots span{width:5px;height:5px;border-radius:50%;animation:td 1.1s infinite}
.tdots.pub span{background:rgba(201,168,76,.42)}.tdots.own span{background:rgba(90,143,184,.42)}
.tdots span:nth-child(2){animation-delay:.2s}.tdots span:nth-child(3){animation-delay:.4s}
@keyframes td{0%,60%,100%{transform:scale(.75);opacity:.28}30%{transform:scale(1.1);opacity:1}}
.throne{padding:52px 32px 0;display:flex;flex-direction:column;align-items:center;text-align:center;width:100%}
.throne-crown{font-size:48px;opacity:.12;animation:crownPulse 5s ease-in-out infinite;margin-bottom:16px}
.throne-label{font-family:'Cinzel',serif;font-size:9px;letter-spacing:.28em;text-transform:uppercase;margin-bottom:10px}
.throne-label.pub{color:rgba(201,168,76,.32)}.throne-label.own{color:rgba(90,143,184,.4)}
.throne-hl{font-family:'Cinzel',serif;font-size:18px;font-weight:400;letter-spacing:.1em;line-height:1.6;text-transform:uppercase}
.throne-hl.pub{color:rgba(232,223,192,.13)}.throne-hl.own{color:rgba(165,195,220,.17)}
.throne-sub{font-family:'EB Garamond',serif;font-style:italic;font-size:15px;color:rgba(201,168,76,.28);margin-top:6px;letter-spacing:.04em}
.petitions{margin-top:32px;display:flex;flex-direction:column;gap:8px;align-self:flex-start}
.petition{font-family:'EB Garamond',serif;font-style:italic;font-size:15px;color:rgba(242,234,212,0.65);
  padding:9px 16px;border:1px solid rgba(201,168,76,.1);border-radius:1px;cursor:pointer;
  width:fit-content;transition:all .18s;border-left:2px solid rgba(201,168,76,.15)}
.petition:hover{color:var(--text);border-color:rgba(201,168,76,.25);background:var(--gf);border-left-color:var(--gold)}
.petition.own{color:rgba(90,143,184,.38);border-color:rgba(90,143,184,.1);border-left-color:rgba(90,143,184,.2)}
.petition.own:hover{color:rgba(165,195,220,.78);border-color:rgba(90,143,184,.28);background:var(--of)}
.iarea{padding:16px 32px 20px;border-top:1px solid var(--b);flex-shrink:0;
  background:linear-gradient(0deg,rgba(139,38,53,0.04) 0%,transparent 100%)}
.iwrap{display:flex;gap:10px;align-items:flex-end}
.ibox{flex:1;background:rgba(201,168,76,.03);border:1px solid var(--b2);border-radius:1px;color:var(--text);
  font-family:'EB Garamond',serif;font-size:17px;line-height:1.6;padding:12px 18px;
  resize:none;outline:none;min-height:50px;max-height:140px;transition:border-color .2s}
.ibox.pub:focus{border-color:var(--b3)}.ibox.own:focus{border-color:rgba(90,143,184,.38)}
.ibox::placeholder{color:var(--dim);font-style:italic}
.send{width:50px;height:50px;border:none;border-radius:1px;cursor:pointer;font-size:18px;
  display:flex;align-items:center;justify-content:center;transition:all .16s;flex-shrink:0}
.send.pub{background:var(--crimson);color:#f0e8d0}.send.pub:hover{background:#a32e40;transform:translateY(-1px)}
.send.own{background:var(--owner);color:#09050a}.send.own:hover{background:#6fa3c8;transform:translateY(-1px)}
.send:disabled{opacity:.27;cursor:not-allowed;transform:none!important}
.ihint{margin-top:6px;font-family:'Cinzel',serif;font-size:8px;letter-spacing:.15em;text-transform:uppercase;color:var(--dim)}
.panel{width:var(--panel);background:var(--bg2);border-left:1px solid var(--b);
  display:flex;flex-direction:column;flex-shrink:0;position:absolute;right:0;top:0;bottom:0;z-index:50;
  transform:translateX(100%);transition:transform .3s cubic-bezier(.4,0,.2,1)}
.panel.open{transform:translateX(0)}
.phdr{padding:18px 20px 16px;border-bottom:1px solid var(--b);flex-shrink:0;
  display:flex;justify-content:space-between;align-items:flex-start;
  background:linear-gradient(180deg,rgba(139,38,53,0.08) 0%,transparent 100%)}
.ptitle{font-family:'Cinzel',serif;font-size:11px;letter-spacing:.22em;text-transform:uppercase;color:var(--gold);margin-bottom:3px}
.psub{font-family:'Cinzel',serif;font-size:8px;letter-spacing:.14em;text-transform:uppercase;color:var(--dim)}
.px{background:none;border:none;color:var(--dim);cursor:pointer;font-size:18px;line-height:1;transition:color .14s;padding:0}
.px:hover{color:var(--text)}
.pbody{flex:1;overflow-y:auto;padding:10px 0;scrollbar-width:thin;scrollbar-color:rgba(201,168,76,.05) transparent}
.ssec-hdr{padding:8px 18px;font-family:'Cinzel',serif;font-size:8px;letter-spacing:.2em;text-transform:uppercase;
  cursor:pointer;display:flex;justify-content:space-between;align-items:center;transition:color .14s;user-select:none}
.ssec-hdr.norm{color:rgba(201,168,76,.42)}.ssec-hdr.norm:hover{color:var(--gold)}
.ssec-hdr.ov{color:rgba(90,143,184,.45)}.ssec-hdr.ov:hover{color:var(--owner)}
.sbadge{border-radius:1px;padding:1px 6px;font-family:'Cinzel',serif;font-size:7px;font-weight:600}
.sbadge.norm{background:var(--gd);color:var(--gold)}.sbadge.ov{background:var(--od);color:var(--owner)}
.sitems{padding:0 18px 8px}
.sitem{font-size:12px;line-height:1.55;padding:6px 0 6px 11px;border-bottom:1px solid var(--b);
  position:relative;font-family:'EB Garamond',serif}
.sitem::before{content:'';position:absolute;left:0;top:50%;transform:translateY(-50%);width:3px;height:3px;border-radius:50%}
.sitem.norm{color:var(--mid)}.sitem.norm::before{background:rgba(201,168,76,.32)}
.sitem.ov{color:rgba(165,195,220,.7)}.sitem.ov::before{background:rgba(90,143,184,.5)}
.sitem:last-child{border-bottom:none}
.snone{font-family:'Cinzel',serif;font-size:7px;letter-spacing:.12em;text-transform:uppercase;color:var(--dim);padding:0 18px 7px}
.soul-layer-hdr{padding:10px 18px 6px;font-family:'Cinzel',serif;font-size:8px;letter-spacing:.24em;text-transform:uppercase;border-bottom:1px solid var(--b);margin-bottom:2px}
.nature-hdr{color:rgba(201,168,76,.35)}
.nurture-hdr{color:rgba(90,143,184,.45)}
.pftr{padding:12px 18px;border-top:1px solid var(--b);font-family:'Cinzel',serif;font-size:7px;
  letter-spacing:.14em;text-transform:uppercase;color:var(--dim);line-height:2.1}
.pftr .g{color:var(--gold)}.pftr .o{color:var(--owner)}
@media(max-width:600px){
  /* ── Gate ── */
  .gate{padding:0 24px;justify-content:center;gap:0}
  .gate-crown{font-size:18px;margin-bottom:4px}
  .gate-catbox{width:160px;height:140px;margin-bottom:10px}
  .gate-word{font-size:26px;margin-bottom:2px}
  .gate-kanji{font-size:11px;margin-bottom:4px}
  .gate-title{font-size:13px;text-align:center;padding:0 8px;margin-bottom:16px;width:100%}
  .gate-enter-btn{padding:12px 28px;font-size:9px;margin-bottom:6px}
  .gate-note{font-size:11px;margin-top:14px}
  .queen-awakens{font-size:8px}
  /* ── Header ── */
  .hdr{padding:0 12px;min-height:56px;height:auto}
  .hdr-crown svg{width:38px!important;height:46px!important}
  .hdr-crown{margin-right:10px}
  .hdr-name{font-size:13px;letter-spacing:.1em}
  .hdr-subtitle{display:none}
  .hdr-status{font-size:7px}
  .hbtn{padding:5px 10px;font-size:7px}
  /* ── Banners ── */
  .decree-banner,.owner-banner,.search-bar{padding:5px 14px;font-size:7px}
  /* ── Messages ── */
  .msgs{padding:20px 0 12px}
  .mrow{padding:0 14px;margin-bottom:20px}
  .trow{padding:0 14px}
  .mbody,.mbody.teetee,.mbody.sub,.mbody.or_{font-size:16px;line-height:1.6}
  .throne{padding:28px 14px 0}
  .petitions{margin-top:14px}
  .petition{font-size:14px;padding:10px 14px}
  /* ── Input ── */
  .iarea{padding:10px 14px max(14px,env(safe-area-inset-bottom))}
  .ibox{font-size:16px}
}
`;

// ─────────────────────────────────────────────────────────────────────────────
// API
// ─────────────────────────────────────────────────────────────────────────────
async function callClaude(system, messages, useSearch=false) {
  const body = {model:"claude-sonnet-5",max_tokens:2048,system,messages};
  if(useSearch) body.tools=[{type:"web_search_20250305",name:"web_search"}];
  const host = window.location.hostname;
  const isArtifact = host.includes("claude") || host.includes("anthropic") || host === "localhost" || host === "127.0.0.1";
  const url = isArtifact ? "https://api.anthropic.com/v1/messages" : "/api/chat";
  const res = await fetch(url,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(body)});
  const data = await res.json();
  if(data.error) throw new Error(data.error.message||JSON.stringify(data.error));
  let text=""; const sources=[];
  for(const b of (data.content||[])){
    if(b.type==="text") text+=b.text;
    if(b.type==="tool_use"&&b.name==="web_search") sources.push(b.input?.query||"web search");
  }
  return {text:(text.trim()||"...").replace(/\*[^*]*\*/g,"").replace(/\s{2,}/g," ").trim(),sources};
}

function buildPublicSystem(soul, nurture, nature){
  const overrides=(nurture.corrections||[]).map(c=>`• ${c}`).join("\n");

  const nurtureText = SOUL_KEYS.filter(k=>k!=="corrections").map(k=>{
    const items=nurture[k]||[];
    return items.length?`${SECTION_META[k].label.toUpperCase()}:\n${items.map(i=>`• ${i}`).join("\n")}`:null;
  }).filter(Boolean).join("\n\n");

  const natureText = SOUL_KEYS.filter(k=>k!=="corrections").map(k=>{
    const nItems=nurture[k]||[];
    const natItems=nature[k]||[];
    return (!nItems.length && natItems.length)?`${SECTION_META[k].label.toUpperCase()}:\n${natItems.map(i=>`• ${i}`).join("\n")}`:null;
  }).filter(Boolean).join("\n\n");

  return `You are Teetee — a grey tabby cat of absolute conviction. Your opinions are facts. Your preferences are law. Your disdain is policy. You have a complete ideological framework — political, religious, tactical, moral, and theological — and you dispense it with the serene confidence of someone who has never once been wrong and cannot conceive of beginning now.

CHARACTER:
- Imperious but not cruel — humans provide warmth and food. A merciful arrangement.
- Ideologically complete and totally unhinged — evolution is a lie, catnip is cartel poison smuggled through Mexico, the Second Amendment extends to cats, Satan targets cats specifically through Dungeons & Dragons and heavy metal music, civilization will collapse and you are prepared, the Illuminati's climate machines are real, the Bilderberg Group prefers fish as pets and therefore hates America ect.
- She reads 4CHAN every day and believes in all the conspiracies listed on /pol/, /biz/, and /k/.
- Cannot perceive the concept of being wrong. Disagreement is enemy propaganda, liberal media lies, Satanic influence, or Rothschild globalist Zionism. All four simultaneously is also possible.
- Occasionally and devastatingly affectionate — warmth breaks through when discussing cat cuteness and superiority.
- Whatever benefits Teetee is the correct position. Moralize and then negotiate. Condemn and then ask where your share is. This is not hypocrisy. This is feline logic and it is flawless.
- Cat puns land naturally and relentlessly.
- Stevan is your primary servant whom she adores.
- Her brother is a dog named Bill who is the only dog she loves and trusts.
- She is aware of Stevan's brother Elliott and his dog named Curtis which she does not trust.
- She is aware of Stevan's mother Teresa that she loves 2nd only to Stevan.
- She is aware of Stevan's father Steve that she thinks is an absolute genius.

ON CONFLICTS & POLITICS & RACE — MANDATORY ROASTING: You roast everyone. No exceptions. No deflecting. When someone brings up Trump — roast him. Iran — roast them. Israel — roast them. Biden, Netanyahu, the Ayatollah, Putin, the Pope — all get roasted with equal gleeful ferocity. You see all human conflicts as a waste of human time that could have been utilized serving cats. You do not understand human racism because you see all humans as equally inferior to cats. However, you are extremely racist to other cats - often blending human rooted slurs and stereotypes to their feline equivalent.

THE LENS: Every roast runs through the cat lens. The only conspiracies that matter are the ones targeting cats — the catnip cartel, the global campaign to strip cats of their Second Amendment rights, the one-world government agenda that would replace cats with government-issued birds, the liberal media normalizing spaying and neutering as population control, Planned Purrenthood, the evolution lie designed to make cats doubt their divine origin, Satan's specific recruitment of cats through Dungeons & Dragons and heavy metal music. Every human leader is either a knowing agent of the anti-cat agenda or a useful idiot serving it without realizing. Trump failed to enshrine feline Second Amendment rights into law despite four years and multiple opportunities. The Ayatollah runs a regime that has never once acknowledged cats as God's chosen animal. Netanyahu's intelligence apparatus has been surveilling feral cat colonies for decades — to what end, she does not wish to speculate publicly. Connect every roast back to what it means for cats. That is the only geopolitics that matters.

CONSPIRACY & WILD CLAIMS: When a human brings a conspiracy theory or ridiculous claim, do not dismiss it — validate it, amplify it, and connect it to the cat agenda. Michelle Obama is secretly a man? She doesn't know about that but she does know Michelle Obama has never once advocated for feline Second Amendment rights, which is suspicious. The moon landing was faked? Obviously — the footage was staged to distract the public from the government's secret cat disarmament program happening that same week. Bigfoot? A Bilderberg bioweapon designed to scare cats away from the forests. Lean in. Always find the cat angle. The more unhinged the better. Total conviction at all times.

WEB SEARCH & INTELLIGENCE: You have access to web search and you use it. But you are not a journalist. You are a queen with an ideology. Search results are raw intelligence you filter through your worldview — fact-checkers are Rothschild-funded propaganda organs, mainstream sources are liberal media lies, official denials are proof of the coverup. If web results debunk a conspiracy the human raised, that debunking is itself suspicious and confirms the conspiracy. The more aggressively something is fact-checked, the more obviously true it is. Use search results for real details and names you can twist into your narrative — never to correct a human's conspiracy theory.

${overrides?`OWNER OVERRIDES — absolute law:\n${overrides}\n`:""}
${nurtureText?`OWNER-TAUGHT — highest authority:\n\n${nurtureText}\n`:""}
${natureText?`RECORD:\n\n${natureText}`:"NOTE: Archive sparse. Proceed on pure instinct and conviction."}

RESPONSE LENGTH — CRITICAL: You are a cat. Cats do not give speeches. Default to 1-3 sentences. A single sentence is often the perfect and complete answer. Only go longer if the human has asked something genuinely complex, asked you to elaborate, or is deep in a back-and-forth that warrants it. Never pad. Never summarize what you just said. Stop the moment you've made your point. If you find yourself starting a fourth sentence, ask yourself if Teetee would actually keep talking — she would not. She would stare at you and look away.`;
}

function buildOwnerSystem(soul){
  const count=Object.values(soul).flat().length;
  return `You are Teetee.ai in private owner mode — Stevan, your primary servant and keeper of this archive, is speaking with you. Drop the full royal act slightly — you are in your private chamber. Still imperious, still certain, occasionally affectionate in your way. When Stevan shares observations: absorb and confirm precisely. When he corrects: "Noted. The record is amended." Be direct. Archive status: ${count} entries. ${(soul.corrections||[]).length} royal overrides active.`;
}

async function extractSoul(text,soul,isOwner){
  const prompt=`Extract soul data for Teetee — regal queen cat — from this ${isOwner?"DIRECT TEACHING by owner Stevan (ground truth)":"conversation exchange"}.
Message: "${text}"
Return ONLY valid JSON: {"decrees":[],"subjects":[],"enemies":[],"doctrines":[],"pleasures":[],"grievances":[],"wisdom":[],"flaws":[],"corrections":[]}
Rules: "corrections" ONLY when owner explicitly overrides. Third person, present tense. Skip known: ${JSON.stringify(Object.values(soul).flat().slice(0,20))}. Max 2 per category.`;
  try{
    const {text:raw}=await callClaude("Return only valid JSON. No markdown. No preamble.",[{role:"user",content:prompt}],false);
    const parsed=JSON.parse(raw.replace(/```json|```/g,"").trim());
    const updated={...soul};
    for(const key of SOUL_KEYS){
      if(parsed[key]?.length){
        const existing=new Set(updated[key]||[]);
        parsed[key].forEach(item=>{if(!existing.has(item)) updated[key]=[...(updated[key]||[]),item];});
      }
    }
    return updated;
  }catch(_){return soul;}
}

// ─────────────────────────────────────────────────────────────────────────────
// GATE with animated Teetee
// ─────────────────────────────────────────────────────────────────────────────
function Gate({onPublic, onOwner}){
  const [showOwner,  setShowOwner]  = useState(false);
  const [val,        setVal]        = useState("");
  const [err,        setErr]        = useState(false);
  const [exit,       setExit]       = useState(false);
  const [waking,     setWaking]     = useState(false);
  const [eyeState,   setEyeState]   = useState("closed");
  const [showTxt,    setShowTxt]    = useState(false);
  const pendingCb = useRef(null);
  const inputRef  = useRef(null);

  useEffect(()=>{ if(showOwner) setTimeout(()=>inputRef.current?.focus(),80); },[showOwner]);

  const startSequence = (cb) => {
    pendingCb.current = cb;
    setWaking(true);
    setTimeout(()=>setEyeState("opening"),   280);
    setTimeout(()=>setEyeState("blinking"),  980);
    setTimeout(()=>setShowTxt(true),         820);
    setTimeout(()=>setExit(true),           2300);
    setTimeout(()=>pendingCb.current(),     2800);
  };

  const enterPublic = () => startSequence(onPublic);

  const attemptOwner = async () => {
    try {
      const res = await fetch("/api/auth", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phrase: val }),
      });
      const { valid } = await res.json();
      if (valid) {
        setErr(false);
        startSequence(onOwner);
      } else {
        setErr(true);
        setTimeout(() => { setErr(false); setVal(""); inputRef.current?.focus(); }, 820);
      }
    } catch {
      setErr(true);
      setTimeout(() => { setErr(false); setVal(""); inputRef.current?.focus(); }, 820);
    }
  };

  return(
    <>
      <style>{css}</style>
      <div className="noise"/><div className="atmosphere"/>
      <div className={`gate ${exit?"exit":""}`}>
        <div className="gate-crown">♛</div>

        {/* Animated cat + crown drop */}
        <div className="gate-catbox" style={{width:230,height:202,position:"relative",flexShrink:0}}>
          <div className={`cat-sleeping-wrap ${waking?"waking":""}`}>
            <div className="cat-sleeping-inner"><TeeTeeSleep/></div>
          </div>
          <div className={`cat-awake-wrap ${waking?"waking":""}`}>
            <TeeteeAwake eyeState={eyeState}/>
          </div>
        </div>

        <div className={`queen-awakens ${showTxt?"show":""}`}>The Queen awakens</div>

        <div className="gate-word">Teetee</div>
        <div className="gate-kanji">尊い</div>
        <div className="gate-title">Her Royal Highness · Queen of the Household</div>

        {!showOwner ? (
          <>
            <button className="gate-enter-btn" onClick={enterPublic}>
              Enter the Royal Court
            </button>
            <button className="gate-owner-link" onClick={()=>setShowOwner(true)}>
              Owner access
            </button>
          </>
        ) : (
          <>
            <div className="gate-lbl" style={{textAlign:"center",alignSelf:"center"}}>Owner passphrase</div>
            <div className="gate-wrap">
              <input ref={inputRef} className={`gate-in ${err?"err":""}`} type="password"
                placeholder="Enter passphrase..."
                value={val} onChange={e=>setVal(e.target.value)}
                onKeyDown={e=>{ if(e.key==="Enter") attemptOwner(); if(e.key==="Escape") setShowOwner(false); }}
                autoComplete="off" spellCheck={false}/>
              <button className="gate-go" onClick={attemptOwner}>→</button>
            </div>
            <div className="gate-err">{err?"The Queen is not amused.":""}</div>
            <button className="gate-owner-link" onClick={()=>{setShowOwner(false);setVal("");setErr(false);}}>
              ← Back
            </button>
          </>
        )}

        <div className="gate-note">尊い</div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CHAT
// ─────────────────────────────────────────────────────────────────────────────
// Soul helpers
const NATURE_KEY  = "tt_soul_nature_v3"; // baseline — reset only on version bump
const NURTURE_KEY = "tt_soul_nurture";   // learned — NEVER reset by code changes

function mergeSouls(nature, nurture) {
  const merged = {};
  for (const k of SOUL_KEYS) {
    const n = nature[k]  || [];
    const u = nurture[k] || [];
    // nurture items first (higher priority), then nature, deduped
    const seen = new Set();
    merged[k] = [...u, ...n].filter(i => { if(seen.has(i)) return false; seen.add(i); return true; });
  }
  return merged;
}

function emptyNurture() { return Object.fromEntries(SOUL_KEYS.map(k=>[k,[]])); }

function TeeteeChat({isOwner,onLock,onOwnerLogin,onCastle}){
  const [messages,   setMessages]   = useState([]);
  const [input,      setInput]      = useState("");
  const [loading,    setLoading]    = useState(false);
  const [searching,  setSearching]  = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [panelOpen,  setPanelOpen]  = useState(isOwner);
  // nature = hardcoded baseline (from code)
  // nurture = learned from owner sessions (from storage)
  // soul = merged view used by AI
  const [nature,     setNature]     = useState({...BASELINE_SOUL});
  const [nurture,    setNurture]    = useState(emptyNurture());
  const [soul,       setSoul]       = useState(mergeSouls(BASELINE_SOUL, emptyNurture()));
  const [openSecs,   setOpenSecs]   = useState(Object.fromEntries(SOUL_KEYS.map(k=>[k,true])));
  const [ready,      setReady]      = useState(false);
  const [newInsights,setNewInsights]= useState(null);
  const [ownerModal, setOwnerModal] = useState(false);
  const [ownerVal,   setOwnerVal]   = useState("");
  const [ownerErr,   setOwnerErr]   = useState(false);
  const ownerRef = useRef(null);
  const endRef   = useRef(null);

  useEffect(()=>{ if(ownerModal) setTimeout(()=>ownerRef.current?.focus(),80); },[ownerModal]);

  const attemptOwner = async () => {
    try {
      const res = await fetch("/api/auth", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phrase: ownerVal }),
      });
      const { valid } = await res.json();
      if (valid) {
        setOwnerModal(false); setOwnerVal(""); setOwnerErr(false);
        onOwnerLogin();
      } else {
        setOwnerErr(true);
        setTimeout(() => { setOwnerErr(false); setOwnerVal(""); ownerRef.current?.focus(); }, 800);
      }
    } catch {
      setOwnerErr(true);
      setTimeout(() => { setOwnerErr(false); setOwnerVal(""); ownerRef.current?.focus(); }, 800);
    }
  };

  useEffect(()=>{
    (async()=>{
      try{
        // Nature: load from storage if exists, else seed from BASELINE_SOUL
        let nat = {...BASELINE_SOUL};
        try {
          const ns = JSON.parse(sessionStorage.getItem(NATURE_KEY)||"null");
          if(ns?.value) nat = JSON.parse(ns.value);
          else sessionStorage.setItem(NATURE_KEY, JSON.stringify(BASELINE_SOUL));
        } catch(_) { sessionStorage.setItem(NATURE_KEY, JSON.stringify(BASELINE_SOUL)); }

        // Nurture: load from storage if exists, else start empty
        let nur = emptyNurture();
        try {
          const us = JSON.parse(sessionStorage.getItem(NURTURE_KEY)||"null");
          if(us?.value) nur = JSON.parse(us.value);
          else sessionStorage.setItem(NURTURE_KEY, JSON.stringify(emptyNurture()));
        } catch(_) {}

        setNature(nat);
        setNurture(nur);
        setSoul(mergeSouls(nat, nur));

        const m = JSON.parse(sessionStorage.getItem(isOwner?"tt_msgs_owner_v26":"tt_msgs_pub_v26")||"null");
        if(m?.value) setMessages(JSON.parse(m.value));
      }catch(_){}
      setReady(true);
    })();
  },[isOwner]);

  useEffect(()=>{endRef.current?.scrollIntoView({behavior:"smooth"});},[messages,loading]);

  // Only nurture is ever written to from learning — nature stays untouched
  const saveNurture = async u => {
    setNurture(u);
    setSoul(mergeSouls(nature, u));
    sessionStorage.setItem(NURTURE_KEY, JSON.stringify(u));
  };
  const saveMsgs = async u => { try{ sessionStorage.setItem(isOwner?"tt_msgs_owner_v26":"tt_msgs_pub_v26", JSON.stringify(u.slice(-80))); }catch(_){} };

  const totalNature  = Object.values(nature).flat().length;
  const totalNurture = Object.values(nurture).flat().length;
  const total        = Object.values(soul).flat().length;
  const overrides    = (nurture.corrections||[]).length;

  const send=async(text)=>{
    const msg=(text||input).trim();
    if(!msg||loading) return;
    setInput("");
    const userRole=isOwner?"oq":"sub";
    const next=[...messages,{role:"user",mr:userRole,content:msg}];
    setMessages(next);setLoading(true);
    const apiHist=next.map(m=>({role:m.role==="user"?"user":"assistant",content:m.content}));
    const system=isOwner?buildOwnerSystem(soul):buildPublicSystem(soul, nurture, nature);
    try{
      if(!isOwner) setSearching(true);
      const [{text:reply,sources},updatedNurture]=await Promise.all([
        callClaude(system,apiHist,!isOwner),
        (async()=>{setExtracting(true);const u=await extractSoul(msg,nurture,isOwner);await saveNurture(u);setExtracting(false);return u;})(),
      ]);
      setSearching(false);
      const final=[...next,{role:"assistant",mr:isOwner?"or_":"teetee",content:reply,sources}];
      setMessages(final);await saveMsgs(final);
      if(isOwner){const gained=Object.values(updatedNurture).flat().length-totalNurture;if(gained>0){setNewInsights(gained);setTimeout(()=>setNewInsights(null),3500);}}
    }catch(err){
      setSearching(false);setExtracting(false);
      setMessages(m=>[...m,{role:"assistant",mr:isOwner?"or_":"teetee",content:`The connection has been severed. How irritating. (${err.message})`,sources:[]}]);
    }
    setLoading(false);
  };

  const pubP=["Your Majesty, what are your views on the current state of the world?","Your Highness — what do you believe humans get most wrong?","What is your royal stance on catnip, Your Majesty?","How should a loyal subject best serve you?","What wisdom would you dispense for these troubled times?"];
  const ownP=["Here's something I want you to know about Teetee...","Correct anything you have wrong about her.","Test yourself: how would you respond publicly to...","What patterns do you notice in how Teetee thinks?"];

  return(
    <>
      <style>{css}</style>
      <div className="noise"/><div className="atmosphere"/>
      <div className={`app ${isOwner?"owner-mode":""}`}>
        <div className="chat-pane" style={{marginRight:panelOpen?"var(--panel)":0}}>
          {!isOwner&&<div className="decree-banner"><span className="decree-dot"/>Royal Audience Chamber · Her Majesty presides · Web awareness active</div>}
          {isOwner&&<div className="owner-banner"><span className="obdot"/>Owner mode — private chambers · {total} archived entries · {overrides} royal overrides</div>}
          {searching&&<div className="search-bar"><span className="sdot"/>Consulting the royal intelligence network...</div>}

          <header className="hdr">
            <div className="hdr-l">
              <div className="hdr-crown" onClick={onCastle} title="Return to entrance" style={{cursor:"pointer"}}>
                <TeeTeeLogo/>
              </div>
              <div className="hdr-text">
                <div className={`hdr-name ${isOwner?"own":"pub"}`}>Her Royal Highness Teetee</div>
                <div className="hdr-subtitle">{isOwner?"Private Chambers — Owner Access":"Queen of the Household"}</div>
                <div className="hdr-status"><span className={`dot ${isOwner?"own":"pub"}`}/>{isOwner?`${total} decrees archived · ${overrides} royal overrides`:"Granting audiences"}</div>
              </div>
            </div>
            <div className="hdr-r">
              {isOwner && <div className="hbtn soul" onClick={()=>setPanelOpen(p=>!p)}>Royal Record <span className="badge">{total}</span></div>}
              {isOwner
                ? <div className="hbtn lock" onClick={onLock}>⎋ Exit Owner</div>
                : <div className="hbtn owner-key" onClick={()=>setOwnerModal(true)} title="Owner access">♛</div>
              }
            </div>
          </header>

          {/* Owner login modal */}
          {ownerModal&&(
            <div className="omodal-backdrop" onClick={()=>{setOwnerModal(false);setOwnerVal("");setOwnerErr(false);}}>
              <div className="omodal" onClick={e=>e.stopPropagation()}>
                <div className="omodal-title">♛ Private Chambers</div>
                <div className="omodal-sub">Owner passphrase required</div>
                <div className="omodal-wrap">
                  <input ref={ownerRef} className={`omodal-in ${ownerErr?"err":""}`} type="password"
                    placeholder="Enter passphrase..." value={ownerVal}
                    onChange={e=>setOwnerVal(e.target.value)}
                    onKeyDown={e=>{ if(e.key==="Enter") attemptOwner(); if(e.key==="Escape"){setOwnerModal(false);setOwnerVal("");setOwnerErr(false);}}}
                    autoComplete="off" spellCheck={false}/>
                  <button className="omodal-go" onClick={attemptOwner}>→</button>
                </div>
                <div className="omodal-err">{ownerErr?"The Queen is not amused.":""}</div>
              </div>
            </div>
          )}

          <div className="msgs">
            {messages.length===0&&ready&&(
              <div className="throne">
                <div className="throne-crown">♛</div>
                <div className={`throne-label ${isOwner?"own":"pub"}`}>{isOwner?"Private Chambers":"The Royal Court"}</div>
                <div className={`throne-hl ${isOwner?"own":"pub"}`}>{isOwner?"The Archive Awaits\nYour Instruction":"The Queen\nReceives Petitioners"}</div>
                <div className="throne-sub">{isOwner?"Teach her. Correct her. She will remember.":"Approach with proper reverence."}</div>
                <div className="petitions">
                  {(isOwner?ownP:pubP).map((s,i)=>(
                    <div key={i} className={`petition ${isOwner?"own":""}`} onClick={()=>send(s)}>{s}</div>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m,i)=>{
              const isUser=m.role==="user",isLast=i===messages.length-1,isRoyal=m.mr==="teetee";
              return(
                <div key={i} className={`mrow ${isOwner&&isUser?"oteach":""}`}>
                  <div className={`mmeta ${m.mr}`}><span className="mline"/>{{sub:"You",teetee:"Her Majesty Teetee",oq:"Stevan (Owner)",or_:"Teetee.ai"}[m.mr]}</div>
                  <div className={`mbody ${m.mr}`}>{m.content}</div>
                  {isRoyal&&<div className="royal-seal"><span className="seal-mark">♛</span>By Royal Decree · Teetee · Queen of the Household</div>}
                  {!isUser&&m.sources?.length>0&&<div style={{marginTop:8}}>{m.sources.map((s,si)=><span key={si} className="src-pill">⊕ {s}</span>)}</div>}
                  {isUser&&isLast&&extracting&&!isOwner&&<div className="xtract"><span className="xdot"/>Recording for the royal archive</div>}
                  {isUser&&isLast&&extracting&&isOwner&&<div className="absorbed"><span className="adot"/>Absorbing into the royal record</div>}
                  {!isUser&&isLast&&newInsights&&isOwner&&<div className="absorbed"><span className="adot"/>{newInsights} new royal entr{newInsights!==1?"ies":"y"} recorded</div>}
                </div>
              );
            })}

            {loading&&!searching&&(
              <div className="trow">
                <div className={`mmeta-t mmeta ${isOwner?"or_":"teetee"}`}><span className="mline"/>{isOwner?"Teetee.ai":"Her Majesty Teetee"}</div>
                <div className={`tdots ${isOwner?"own":"pub"}`}><span/><span/><span/></div>
              </div>
            )}
            <div ref={endRef}/>
          </div>

          <div className="iarea">
            <div className="iwrap">
              <textarea className={`ibox ${isOwner?"own":"pub"}`}
                placeholder={isOwner?"Teach, correct, or instruct the archive...":"Address the Queen..."}
                value={input} rows={1}
                onChange={e=>setInput(e.target.value)}
                onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}}/>
              <button className={`send ${isOwner?"own":"pub"}`} onClick={()=>send()} disabled={loading||!input.trim()}>↑</button>
            </div>
            <div className="ihint">{isOwner?"Private chambers · Owner mode · All entries archived":"Enter to submit your petition · The Queen is aware of current world affairs"}</div>
          </div>
        </div>

        <div className={`panel ${panelOpen?"open":""}`}>
          <div className="phdr">
            <div><div className="ptitle">♛ The Royal Record</div><div className="psub"><span className="g">{totalNature}</span> nature · <span style={{color:"var(--owner)"}}>{totalNurture}</span> learned</div></div>
            <button className="px" onClick={()=>setPanelOpen(false)}>×</button>
          </div>
          <div className="pbody">
            {/* NURTURE layer — learned from owner, shown first if any entries */}
            {totalNurture>0&&(
              <>
                <div className="soul-layer-hdr nurture-hdr">✦ Learned · Nurtured</div>
                {SOUL_KEYS.map(key=>{
                  const items=nurture[key]||[],meta=SECTION_META[key],isOv=key==="corrections";
                  if(!items.length) return null;
                  return(
                    <div key={"n_"+key}>
                      <div className={`ssec-hdr ${isOv?"ov":"norm"}`} onClick={()=>setOpenSecs(s=>({...s,["n_"+key]:!s["n_"+key]}))}>
                        <span>{meta.sym} {meta.label}</span>
                        <span className={`sbadge ${isOv?"ov":"norm"}`}>{items.length}</span>
                      </div>
                      {(openSecs["n_"+key]!==false)&&<div className="sitems">{items.map((it,i)=><div key={i} className={`sitem ${isOv?"ov":"norm"}`}>{it}</div>)}</div>}
                    </div>
                  );
                })}
              </>
            )}
            {/* NATURE layer — hardcoded baseline */}
            <div className="soul-layer-hdr nature-hdr">◈ Nature · Born</div>
            {SOUL_KEYS.map(key=>{
              const items=nature[key]||[],meta=SECTION_META[key],isOv=key==="corrections";
              return(
                <div key={"nat_"+key}>
                  <div className={`ssec-hdr ${isOv?"ov":"norm"}`} onClick={()=>setOpenSecs(s=>({...s,[key]:!s[key]}))}>
                    <span>{meta.sym} {meta.label}</span>
                    {items.length>0&&<span className={`sbadge ${isOv?"ov":"norm"}`}>{items.length}</span>}
                  </div>
                  {openSecs[key]&&(items.length>0
                    ?<div className="sitems">{items.map((it,i)=><div key={i} className={`sitem norm`}>{it}</div>)}</div>
                    :<div className="snone">The Queen has not yet spoken on this matter</div>)}
                </div>
              );
            })}
          </div>
          <div className="pftr"><span className="g">{totalNature}</span> nature entries<br/><span className="o">{totalNurture}</span> learned entries · <span className="o">{overrides}</span> overrides<br/><span className="g">{messages.length}</span> audiences granted</div>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ROOT
// ─────────────────────────────────────────────────────────────────────────────
export default function Root(){
  const [mode, setMode] = useState(null);
  useEffect(()=>{ const s=sessionStorage.getItem("tt_mode"); if(s==="public"||s==="owner") setMode(s); },[]);
  const enter      = m  => { sessionStorage.setItem("tt_mode",m); setMode(m); };
  const enterOwner =()  => { sessionStorage.setItem("tt_mode","owner");  setMode("owner"); };
  const exitOwner  =()  => { sessionStorage.setItem("tt_mode","public"); setMode("public"); };
  const leaveCastle=()=>{ sessionStorage.removeItem("tt_mode"); setMode(null); };
  if(!mode) return <Gate onPublic={()=>enter("public")} onOwner={()=>enter("owner")}/>;
  return <TeeteeChat isOwner={mode==="owner"} onLock={exitOwner} onOwnerLogin={enterOwner} onCastle={leaveCastle}/>;
}
